/**
 * Chart Configuration Compression Utilities
 *
 * This utility provides functions to compress and decompress chart configuration JSON objects
 * by mapping long, repetitive keys to short identifiers.
 * This approach (Option C) reduces payload size by ~40-60% without losing data
 * and without requiring binary protocols.
 */

// Mapping of long keys to short IDs.
// INVARIANT: every value must be unique, or REVERSE_KEY_MAP collapses two keys onto one
// short id and compress→decompress silently loses data. Guarded by chart-config-compression.test.ts.
export const KEY_MAP: Record<string, string> = {
  // Structural keys
  layout: 'l',
  charts: 'c',
  panes: 'p',
  sources: 's',
  type: 't',
  id: 'i',
  zorder: 'z',
  state: 'st',
  visible: 'v',
  symbol: 'sb',
  interval: 'iv',
  inputs: 'in',
  styles: 'sy',
  stateVersion: 'sv',
  ownerSource: 'os',
  mainSourceId: 'msi',
  stretchFactor: 'sf',

  // Style/Property keys
  color: 'co',
  upColor: 'uc',
  downColor: 'dc',
  borderColor: 'bc',
  wickColor: 'wc',
  wickUpColor: 'wuc',
  wickDownColor: 'wdc',
  borderUpColor: 'buc',
  borderDownColor: 'bdc',
  drawWick: 'dw',
  drawBorder: 'db',
  drawBody: 'dbo',
  lineWidth: 'lw',
  lineStyle: 'ls',
  linestyle: 'lss', // TradingView uses both lowercase and camelCase sometimes
  linewidth: 'lww',
  fontSize: 'fs',
  transparency: 'tr',
  plottype: 'pt',
  trackPrice: 'tp',
  display: 'dp',

  // High-level config keys
  timeScale: 'ts',
  chartProperties: 'cp',
  paneProperties: 'pp',
  scalesProperties: 'scp',
  axisProperties: 'ap',
  legendProperties: 'lp',
  background: 'bg',
  backgroundType: 'bgt',
  gridLinesMode: 'glm',
  crossHairProperties: 'chp',
  sessions: 'ses',
  timezone: 'tz',
  version: 'vsn',

  // Indicators/Studies
  studyId: 'sid',
  metaInfo: 'mi',
  defaults: 'df',
  palettes: 'pa',
  graphics: 'gr',
  plots: 'plo',
  ohlcPlots: 'ohl',
  filledAreas: 'fa',
  inputInfo: 'ii',
  defval: 'dv',
  description: 'desc',
  shortDescription: 'sd',
  is_price_study: 'ips',
  isTVScript: 'its',
  isTVScriptStub: 'itss',
  is_hidden_study: 'ihs',
  packageId: 'pid',
  productId: 'prid',
  fullId: 'fid',
  shortId: 'shid',
  scripts: 'scs',

  // Plotting
  histogramBase: 'hb',
  joinPoints: 'jp',
  priceRange: 'pr',
  isAutoScale: 'ias',
  isPercentage: 'ip',
  isIndexedTo100: 'ii100',
  isLog: 'il',
  isLockScale: 'ils',
  topMargin: 'tm',
  bottomMargin: 'bm',
  alignLabels: 'al',
  logFormula: 'lf',
  logicalOffset: 'lo',
  coordOffset: 'coo',
  hasCalculatedPriceRange: 'hcpr',

  // Scales/Axes
  rightAxisesState: 'ras',
  leftAxisesState: 'las',
  overlayPriceScales: 'ops',
  priceAxisProperties: 'pap',
  priceScaleRatio: 'psr',
  isCollapsed: 'ic',
  isMaximized: 'im',
  mode: 'md',
  isInverted: 'inv',

  m_priceRange: 'mpr',
  m_isAutoScale: 'mias',
  m_isPercentage: 'mip',
  m_isLog: 'mil',
  m_isLockScale: 'mils',
  m_isInverted: 'mii',
  m_topMargin: 'mtm',
  m_bottomMargin: 'mbm',
  m_barSpacing: 'mbs',
  m_rightOffset: 'mro',

  // Visibility / Events
  intervalsVisibilities: 'ivv',
  ticks: 'tk',
  seconds: 'sec',
  minutes: 'min',
  hours: 'hr',
  days: 'day',
  weeks: 'wk',
  months: 'mo',
  ranges: 'rng',
  secondsFrom: 'sfr',
  secondsTo: 'stt',

  // Style keys (additional)
  upColorProjection: 'ucp',
  downColorProjection: 'dcp',
  borderUpColorProjection: 'bucp',
  borderDownColorProjection: 'bdcp',
  color1: 'c1',
  color2: 'c2',
  linecolor: 'lco',
  textcolor: 'tco',
  labelColor: 'lc',
  baselineColor: 'blc',
  topLineColor: 'tlc',
  bottomLineColor: 'blc2',
  topFillColor1: 'tfc1',
  topFillColor2: 'tfc2',
  bottomFillColor1: 'bfc1',
  bottomFillColor2: 'bfc2',

  // Others
  points: 'pts',
  time_t: 'tt',
  offset: 'off',
  price: 'pri',
  adjustedToSplitTime: 'atst',
  alwaysShowStats: 'ass',
  showMiddlePoint: 'smp',
  showPriceLabels: 'spl',
  showPriceRange: 'spr',
  showPercentPriceRange: 'sppr',
  showPipsPriceRange: 'sppir',
  showBarsRange: 'sbr',
  showDateTimeRange: 'sdtr',
  showDistance: 'sdi',
  showAngle: 'san',
  statsPosition: 'sp',
  symbolStateVersion: 'ssv',
  zOrderVersion: 'zov',
  sources_: 'srr', // Avoid collision
}

// Reverse mapping for decompression
const REVERSE_KEY_MAP: Record<string, string> = Object.entries(KEY_MAP).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
)

/**
 * Recursively maps keys in an object based on the provided map.
 */
function mapKeys(obj: any, map: Record<string, string>): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => mapKeys(item, map))
  }

  const result: any = {}
  for (const key in obj) {
    const newKey = map[key] || key
    result[newKey] = mapKeys(obj[key], map)
  }
  return result
}

/**
 * Option C: Compress JSON by mapping long keys to short identifiers.
 */
export function compressChartConfig(config: any): any {
  return mapKeys(config, KEY_MAP)
}

/**
 * Option C: Decompress JSON by restoring short identifiers to long keys.
 */
export function decompressChartConfig(compressedConfig: any): any {
  return mapKeys(compressedConfig, REVERSE_KEY_MAP)
}

/**
 * Helper to get the compression ratio and sizes.
 */
export function getCompressionStats(config: any) {
  const original = JSON.stringify(config)
  const compressed = JSON.stringify(compressChartConfig(config))

  const originalSize = new TextEncoder().encode(original).length
  const compressedSize = new TextEncoder().encode(compressed).length

  return {
    originalSizeBytes: originalSize,
    compressedSizeBytes: compressedSize,
    ratio: ((compressedSize / originalSize) * 100).toFixed(2) + '%',
    reduction: (100 - (compressedSize / originalSize) * 100).toFixed(2) + '%',
  }
}

// Prefix to distinguish GZIP-compressed content from legacy key-map-only content
const GZ_PREFIX = 'gz:'

/**
 * Save path: key-map compress → GZIP → base64 string with "gz:" prefix.
 * ~79% reduction vs raw JSON.
 */
export async function compressAndZipChartConfig(config: any): Promise<string> {
  const zipped = await zipChartConfig(compressChartConfig(config))
  return GZ_PREFIX + zipped
}

/**
 * Restore path: detects format and decompresses accordingly.
 * - "gz:" prefix → GZIP + key-map (new format)
 * - plain JSON string → key-map only (legacy format)
 * Returns the original JSON string ready for TradingView.
 */
export async function decompressChartContent(content: string): Promise<string> {
  if (content.startsWith(GZ_PREFIX)) {
    const keyMapped = await unzipChartConfig(content.slice(GZ_PREFIX.length))
    return JSON.stringify(decompressChartConfig(keyMapped))
  }
  // Legacy: plain JSON with key-mapping only
  return JSON.stringify(decompressChartConfig(JSON.parse(content)))
}

/**
 * Option B: Binary Compression using Browser's CompressionStream (GZIP)
 * Note: Returns a Uint8Array or Base64 string.
 */
export async function zipChartConfig(config: any): Promise<string> {
  const stream = new Blob([JSON.stringify(config)], { type: 'application/json' })
    .stream()
    .pipeThrough(new CompressionStream('gzip'))

  const response = new Response(stream)
  const blob = await response.blob()
  const arrayBuffer = await blob.arrayBuffer()

  // Convert to Base64 for storage as string
  return btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)))
}

/**
 * Option B: Decompress GZIP Base64 string back to JSON
 */
export async function unzipChartConfig(base64: string): Promise<any> {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))

  const response = new Response(stream)
  const text = await response.text()
  return JSON.parse(text)
}
